/**
 * Helpers for the custom HTML email block.
 *
 * Three pure functions, no imports:
 * - extractEmailHtmlDocument: turns a pasted full HTML document into body
 *   content plus hoisted styles, so AI generated emails paste cleanly.
 * - scanEmailHtmlCompatibility: non blocking warnings about HTML and CSS that
 *   email clients handle differently than browsers.
 * - findUnsafeEmailHtml: the frontend mirror of the backend otc-html-guard
 *   layer. Message strings MUST stay identical to that layer's; the paired
 *   test suites (tests/email-html.test.js and tests/html-guard.test.js) run
 *   the same input table against both, which is the only mechanical guard
 *   against drift.
 *
 * Detection is parser based via DOMParser, never regex based. Regexes miss
 * payloads like <svg/onload=alert(1)> (slash separator) and
 * href="javascript&#58;..." (entity encoded colon); a parser normalizes both
 * before we look. The single exception is the document tags rule: DOMParser
 * always synthesizes html and body elements, so that one rule tests the RAW
 * string before parsing.
 */

// Mirrors FORBIDDEN_ELEMENTS in backend/layers/html-guard/nodejs/otc-html-guard/htmlGuard.mjs
const FORBIDDEN_ELEMENTS = {
  script: 'Script tags are not allowed in custom HTML',
  iframe: 'Iframe tags are not allowed in custom HTML',
  object: 'Object tags are not allowed in custom HTML',
  embed: 'Embed tags are not allowed in custom HTML',
  base: 'Base tags are not allowed in custom HTML',
  applet: 'Applet tags are not allowed in custom HTML',
  frame: 'Frame tags are not allowed in custom HTML',
  frameset: 'Frameset tags are not allowed in custom HTML',
  noscript: 'Noscript tags are not allowed in custom HTML',
}

const URL_ATTRIBUTES = ['href', 'src', 'xlink:href', 'action', 'formaction', 'background', 'poster']

// Inline images are legitimate in email. SVG is excluded because it can carry script.
const ALLOWED_DATA_PREFIXES = [
  'data:image/png',
  'data:image/jpeg',
  'data:image/jpg',
  'data:image/gif',
  'data:image/webp',
]

const MESSAGES = {
  eventHandler: 'Inline event handlers such as onclick are not allowed in custom HTML',
  metaRefresh: 'Meta refresh is not allowed in custom HTML',
  scriptUrl: 'javascript: URLs are not allowed in custom HTML',
  dataUrl: 'data: URLs are not allowed in custom HTML',
  cssExpression: 'CSS expressions are not allowed in custom HTML',
  documentTags: 'Custom HTML blocks must contain body content only, without html, head, or body tags',
}

const DOCUMENT_TAG_PATTERN = /<\s*(html|head|body)\b/i

// Gmail clips messages larger than roughly 102 KB.
export const GMAIL_CLIP_BYTES = 102400

function normalizeUrl(value) {
  return String(value == null ? '' : value)
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0020\u007F]/g, '')
    .trim()
    .toLowerCase()
}

function hasCssExpression(css) {
  const value = String(css == null ? '' : css).toLowerCase()
  return value.includes('expression(') || value.includes('javascript:')
}

/**
 * Normalize a pasted value. If it is a fragment, pass it through untouched.
 * If it is a complete document (contains html, head, or body tags), extract
 * the body content, hoist style elements, drop external stylesheets, and
 * report what happened in plain language notices.
 *
 * @param {string} input
 * @returns {{ html: string, hoistedStyles: string, notices: string[] }}
 */
export function extractEmailHtmlDocument(input) {
  const raw = String(input == null ? '' : input)

  if (!DOCUMENT_TAG_PATTERN.test(raw)) {
    return { html: raw, hoistedStyles: '', notices: [] }
  }

  const doc = new DOMParser().parseFromString(raw, 'text/html')
  const notices = []

  const styleEls = Array.from(doc.querySelectorAll('style'))
  const hoistedStyles = styleEls.map(el => el.textContent || '').filter(s => s.trim()).join('\n')
  if (styleEls.length > 0) {
    notices.push(styleEls.length === 1
      ? 'Moved 1 style block into the template styles'
      : `Moved ${styleEls.length} style blocks into the template styles`)
    styleEls.forEach(el => el.remove())
  }

  const linkEls = Array.from(doc.querySelectorAll('link[rel="stylesheet"]'))
  if (linkEls.length > 0) {
    notices.push('Removed external stylesheet links. Email clients do not load them; use inline styles or style blocks instead')
    linkEls.forEach(el => el.remove())
  }

  notices.push('Removed the surrounding html, head, and body tags')

  return {
    html: (doc.body ? doc.body.innerHTML : '').trim(),
    hoistedStyles,
    notices,
  }
}

// Each rule fires at most once. level is 'warning' or 'info'.
const COMPATIBILITY_RULES = [
  { level: 'warning', pattern: /<\s*script\b/i, message: 'JavaScript does not run in email clients' },
  { level: 'warning', pattern: /display\s*:\s*grid/i, message: 'CSS grid is not supported by Outlook and several other email clients' },
  { level: 'warning', pattern: /display\s*:\s*(inline-)?flex/i, message: 'Flexbox layouts render inconsistently across email clients' },
  { level: 'warning', pattern: /<\s*link[^>]+stylesheet/i, message: 'External stylesheets are not loaded by most email clients. Use inline styles instead' },
  { level: 'warning', pattern: /@import\b/i, message: '@import is not supported by most email clients' },
  { level: 'warning', pattern: /@font-face|fonts\.googleapis\.com|fonts\.gstatic\.com/i, message: 'Custom web fonts are not supported by Outlook and will fall back to a default font' },
  { level: 'warning', pattern: /position\s*:\s*(absolute|fixed|sticky)/i, message: 'Absolute and fixed positioning are not supported by most email clients' },
  { level: 'warning', pattern: /<\s*(video|audio|canvas|svg)\b/i, message: 'Video, audio, canvas, and SVG content does not render in many email clients' },
  { level: 'warning', pattern: /var\(\s*--/i, message: 'CSS custom properties are not supported by Outlook' },
  { level: 'warning', pattern: /<\s*form\b/i, message: 'Forms are blocked by most email clients' },
  { level: 'warning', pattern: /<\s*img\b[^>]*\ssrc\s*=\s*["']?\s*data:/i, message: 'Embedded base64 images are not shown by Gmail or Outlook. Upload the image with the image block or host it at a public https URL' },
  // A src that is not absolute http(s), protocol relative, cid, data, or a merge variable is a relative path with nothing to resolve against in a mail client.
  { level: 'warning', pattern: /<\s*img\b[^>]*\ssrc\s*=\s*["']?\s*(?!https?:|\/\/|cid:|data:|\{\{)[^"'\s>]/i, message: 'Image paths must be full public https URLs. Relative paths and local files show as broken images in every email client' },
  { level: 'info', pattern: /calc\s*\(/i, message: 'calc() is not supported by Outlook' },
  { level: 'info', pattern: /background-image\s*:/i, message: 'Background images need a VML fallback to show in Outlook' },
]

/**
 * Non blocking compatibility warnings. Scans block HTML plus hoisted styles.
 *
 * @param {string} html
 * @param {string} hoistedStyles
 * @returns {{ level: string, message: string }[]}
 */
export function scanEmailHtmlCompatibility(html, hoistedStyles) {
  const combined = `${html || ''}\n${hoistedStyles || ''}`
  const results = COMPATIBILITY_RULES
    .filter(rule => rule.pattern.test(combined))
    .map(rule => ({ level: rule.level, message: rule.message }))

  if (new Blob([html || '']).size > GMAIL_CLIP_BYTES) {
    results.push({
      level: 'warning',
      message: 'This HTML is over 102 KB. Gmail clips messages this large and shows a View entire message link',
    })
  }

  return results
}

/**
 * Frontend mirror of the backend guard. Returns problem messages, empty when
 * the HTML is acceptable. Saving is blocked while this returns anything, so
 * the customer sees the problem before the round trip; the backend runs the
 * authoritative copy of the same rules.
 *
 * @param {string} html
 * @param {string} hoistedStyles
 * @returns {string[]}
 */
export function findUnsafeEmailHtml(html, hoistedStyles) {
  const found = new Set()
  const raw = String(html == null ? '' : html)

  if (!raw && !hoistedStyles) {
    return []
  }

  // DOMParser synthesizes html and body on every parse, so this one rule must
  // test the raw string before parsing.
  if (DOCUMENT_TAG_PATTERN.test(raw)) {
    found.add(MESSAGES.documentTags)
  }

  const doc = new DOMParser().parseFromString(raw, 'text/html')

  Array.from(doc.querySelectorAll('*')).forEach(el => {
    const tag = el.tagName.toLowerCase()

    if (FORBIDDEN_ELEMENTS[tag]) {
      found.add(FORBIDDEN_ELEMENTS[tag])
    }

    if (tag === 'style' && hasCssExpression(el.textContent)) {
      found.add(MESSAGES.cssExpression)
    }

    if (tag === 'meta') {
      const httpEquiv = el.getAttribute('http-equiv')
      if (String(httpEquiv == null ? '' : httpEquiv).trim().toLowerCase() === 'refresh') {
        found.add(MESSAGES.metaRefresh)
      }
    }

    Array.from(el.attributes).forEach(attribute => {
      const attr = attribute.name.toLowerCase()
      const value = attribute.value

      if (attr.startsWith('on')) {
        found.add(MESSAGES.eventHandler)
      }

      if (attr === 'style' && hasCssExpression(value)) {
        found.add(MESSAGES.cssExpression)
      }

      if (URL_ATTRIBUTES.includes(attr)) {
        const url = normalizeUrl(value)

        if (url.startsWith('javascript:') || url.startsWith('vbscript:')) {
          found.add(MESSAGES.scriptUrl)
        }

        if (url.startsWith('data:')) {
          const isAllowedImage = attr === 'src' && ALLOWED_DATA_PREFIXES.some(prefix => url.startsWith(prefix))
          if (!isAllowedImage) {
            found.add(MESSAGES.dataUrl)
          }
        }
      }
    })
  })

  if (hoistedStyles && hasCssExpression(hoistedStyles)) {
    found.add(MESSAGES.cssExpression)
  }

  return Array.from(found)
}

// ---------------------------------------------------------------------------
// Fluid widths for the preview. Mirrors fluidizeFixedWidths / fluidizeSegment
// in the custom-email-template-renderer lambda byte for byte (guarded by
// tests/email-html-block-fluid.test.mjs), so the console preview shows the
// block exactly as the renderer sends it: any table, div or image at least
// 400px wide becomes width:100% with a pixel max-width, comments untouched,
// cells never rewritten.
// ---------------------------------------------------------------------------
export const FLUIDIZE_MIN_PX = 400

export function fluidizeFixedWidths(html) {
  return html
    .split(/(<!--[\s\S]*?-->)/)
    .map((segment, index) => (index % 2 === 1 ? segment : fluidizeSegment(segment)))
    .join('');
}

function fluidizeSegment(html) {
  return html.replace(/<(table|div|img)\b([^>]*)>/gi, (tag, name, rawAttrs) => {
    const selfClosing = /\/\s*$/.test(rawAttrs);
    const attrs = selfClosing ? rawAttrs.replace(/\s*\/\s*$/, '') : rawAttrs;
    const attrMatch = attrs.match(/\swidth\s*=\s*["']?(\d+)(?:px)?["']?/i);
    const styleMatch = attrs.match(/\sstyle\s*=\s*(["'])([\s\S]*?)\1/i);
    const styleValue = styleMatch ? styleMatch[2] : '';
    const styleWidthMatch = styleValue.match(/(?:^|;)\s*width\s*:\s*(\d+)px\s*(!important)?\s*(?=;|$)/i);

    const attrPx = attrMatch ? Number(attrMatch[1]) : 0;
    const stylePx = styleWidthMatch ? Number(styleWidthMatch[1]) : 0;
    const px = Math.max(attrPx, stylePx);
    if (px < FLUIDIZE_MIN_PX) {
      return tag;
    }

    const bang = styleWidthMatch && styleWidthMatch[2] ? ' !important' : '';
    const isImg = name.toLowerCase() === 'img';
    const fluid = `width: 100%${bang}; max-width: ${px}px${bang}${isImg ? `; height: auto${bang}` : ''}`;

    let newStyle;
    if (styleWidthMatch) {
      newStyle = styleValue.replace(styleWidthMatch[0], match => match.replace(/width\s*:\s*\d+px\s*(?:!important)?/i, fluid));
    } else {
      newStyle = styleValue.trim() ? `${fluid}; ${styleValue.trim()}` : fluid;
    }
    newStyle = newStyle.replace(/(^|;)(\s*)min-width\s*:\s*(\d+)px(\s*!important)?(?=\s*(?:;|$))/gi,
      (m, sep, ws, n, imp) => Number(n) >= FLUIDIZE_MIN_PX ? `${sep}${ws}min-width: 0${imp || ''}` : m);

    const newAttrs = styleMatch
      ? attrs.replace(styleMatch[0], ` style=${styleMatch[1]}${newStyle}${styleMatch[1]}`)
      : `${attrs} style="${newStyle}"`;
    return `<${name}${newAttrs}${selfClosing ? ' /' : ''}>`;
  });
}
